import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Paper, Typography, Box, Grid, Card, CardContent,
  Select, MenuItem, FormControl, InputLabel, CircularProgress,
  TextField, Divider, Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartTooltip,
  ResponsiveContainer, Cell, LabelList,
} from "recharts";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import PeopleIcon from "@material-ui/icons/People";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SentimentDissatisfiedIcon from "@material-ui/icons/SentimentDissatisfied";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CancelIcon from "@material-ui/icons/Cancel";
import { format } from "date-fns";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

const useStyles = makeStyles(theme => ({
  root: { padding: "24px", display: "flex", flexDirection: "column", gap: 20, minHeight: "100vh", background: theme.palette.type === "dark" ? "#0B0F0C" : "radial-gradient(circle at 5% 0%, rgba(0,219,117,.08), transparent 24%), #F5F8F6" },
  hero: { background: "linear-gradient(118deg, #050806 0%, #101713 62%, #142A1E 100%)", borderRadius: 22, padding: "28px 32px", color: "#fff", boxShadow: "0 20px 48px rgba(5,8,6,.20)", display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", overflow: "hidden" },
  heroTitle: { fontWeight: 800, fontSize: "1.8rem", letterSpacing: "-.5px" },
  heroSubtitle: { opacity: .72, fontSize: ".9rem", marginTop: 5 },
  heroMark: { width: 54, height: 54, borderRadius: 16, background: "rgba(0,219,117,.13)", border: "1px solid rgba(0,219,117,.25)", display: "flex", alignItems: "center", justifyContent: "center", color: "#00DB75" },
  filterCard: { padding: "18px 20px", borderRadius: 16, border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.08)" : "#DEE8E2"}`, boxShadow: "0 10px 28px rgba(10,35,22,.055)" },
  filterTop: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14, flexWrap: "wrap" },
  filterTitle: { fontSize: ".78rem", fontWeight: 800, letterSpacing: ".9px", textTransform: "uppercase", color: "#008C4A" },
  filterStatus: { fontSize: ".74rem", color: theme.palette.type === "dark" ? "rgba(255,255,255,.55)" : "#69766E", padding: "5px 9px", borderRadius: 20, background: theme.palette.type === "dark" ? "rgba(255,255,255,.05)" : "#F2F6F4" },
  filterBar: { display: "grid", gridTemplateColumns: "minmax(220px, 1.5fr) minmax(150px, .75fr) minmax(150px, .75fr)", gap: 12, alignItems: "center", "@media (max-width: 760px)": { gridTemplateColumns: "1fr" } },
  statCard: {
    borderRadius: 16,
    flex: 1,
    minWidth: 150,
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 10px 28px rgba(10,35,22,.06)",
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.07)" : "#E1E9E4"}`,
  },
  statValue: { fontSize: "2rem", fontWeight: 800, lineHeight: 1.1 },
  statLabel: { fontSize: "0.75rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", opacity: 0.7, marginTop: 4 },
  statIcon: { position: "absolute", right: 12, top: 12, opacity: 0.15, fontSize: "3rem" },
  sectionCard: { padding: 20, borderRadius: 18, height: "100%", border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.07)" : "#E1E9E4"}`, boxShadow: "0 10px 30px rgba(10,35,22,.055)" },
  sectionTitle: { fontSize: "0.75rem", fontWeight: 800, letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: theme.spacing(1.5), color: "#008C4A" },
  flowRow: {
    display: "flex", alignItems: "center", padding: "10px 0",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,.07)" : "#EDF2EF"}`,
    gap: theme.spacing(1.5),
  },
  flowName: { flex: 1, fontWeight: 600, fontSize: "0.88rem" },
  badge: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    borderRadius: 20, padding: "2px 10px", fontSize: "0.75rem", fontWeight: 700,
  },
  rateBar: { height: 6, borderRadius: 3, background: "rgba(0,0,0,0.08)", overflow: "hidden", flex: 1 },
  rateBarFill: { height: "100%", borderRadius: 3, background: "linear-gradient(90deg, #00C97A, #00875A)", transition: "width 0.6s ease" },
}));

const StatCard = ({ label, value, icon: Icon, color, sub }) => {
  const classes = useStyles();
  return (
    <Card className={classes.statCard} style={{ borderTop: `4px solid ${color}` }}>
      <CardContent style={{ paddingBottom: 12 }}>
        <Typography className={classes.statValue} style={{ color }}>{value}</Typography>
        <Typography className={classes.statLabel}>{label}</Typography>
        {sub && <Typography style={{ fontSize: "0.72rem", opacity: 0.55, marginTop: 2 }}>{sub}</Typography>}
        <Icon className={classes.statIcon} style={{ fontSize: "3.5rem", color }} />
      </CardContent>
    </Card>
  );
};

const COLORS = { sent: "#17231C", respondedAt: "#00DB75" };

const FloupDashboard = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFlow, setSelectedFlow] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const loadMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedFlow !== "all") params.append("flowId", selectedFlow);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      const { data: res } = await api.get(`/follow-up/metrics?${params}`);
      setData(res);
    } catch (err) {
      console.error("Erro ao carregar métricas:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedFlow, startDate, endDate]);

  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const iv = setInterval(loadMetrics, 60000);
    return () => clearInterval(iv);
  }, [loadMetrics]);

  const s = data?.summary || {};

  return (
    <MainContainer>
      <MainHeader>
        <Title>Painel Follow Up Estratiza</Title>
      </MainHeader>

      <div className={classes.root}>
        <Paper className={classes.hero}>
          <Box style={{ position: "relative", zIndex: 1 }}>
            <Typography className={classes.heroTitle}>Painel Follow Up</Typography>
            <Typography className={classes.heroSubtitle}>Visão executiva da cadência, retornos e desempenho dos fluxos.</Typography>
          </Box>
          <Box className={classes.heroMark}><TrendingUpIcon style={{ fontSize: 30 }} /></Box>
        </Paper>
        {/* Filters */}
        <Paper className={classes.filterCard}>
          <div className={classes.filterTop}>
            <Typography className={classes.filterTitle}>Período e fluxo analisado</Typography>
            <Typography className={classes.filterStatus}>Atualização automática a cada 60 segundos</Typography>
          </div>
          <div className={classes.filterBar}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Filtrar por fluxo</InputLabel>
              <Select value={selectedFlow} onChange={e => setSelectedFlow(e.target.value)} label="Filtrar por fluxo">
                <MenuItem value="all">Todos os fluxos</MenuItem>
                {(data?.flows || []).map(f => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Data início" type="date" size="small" variant="outlined"
              value={startDate} onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }} fullWidth
            />
            <TextField
              label="Data fim" type="date" size="small" variant="outlined"
              value={endDate} onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }} fullWidth
            />
          </div>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" padding={6}><CircularProgress /></Box>
        ) : (
          <>
            {/* KPI Cards */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <StatCard label="Total iniciados" value={s.total || 0} icon={PeopleIcon} color="#17231C" />
              <StatCard label="Em andamento" value={s.active || 0} icon={AutorenewIcon} color="#F59E0B"
                sub="No fluxo agora" />
              <StatCard label="Responderam" value={s.responded || 0} icon={CheckCircleIcon} color="#00C97A"
                sub="Convertidos" />
              <StatCard label="Sem sucesso" value={s.completed || 0} icon={SentimentDissatisfiedIcon} color="#EF4444"
                sub="Fluxo finalizado sem resposta" />
              <StatCard label="Cancelados" value={s.cancelled || 0} icon={CancelIcon} color="#94A3B8" />
              <Card className={classes.statCard} style={{ borderTop: "4px solid #00C97A", minWidth: 180 }}>
                <CardContent style={{ paddingBottom: 12 }}>
                  <Typography className={classes.statValue} style={{ color: "#00C97A" }}>
                    {s.responseRate || 0}%
                  </Typography>
                  <Typography className={classes.statLabel}>Taxa de retorno</Typography>
                  <Typography style={{ fontSize: "0.72rem", opacity: 0.55, marginTop: 2 }}>
                    Responderam vs finalizaram
                  </Typography>
                  <Box mt={1}>
                    <div className={classes.rateBar}>
                      <div className={classes.rateBarFill} style={{ width: `${s.responseRate || 0}%` }} />
                    </div>
                  </Box>
                  <TrendingUpIcon className={classes.statIcon} style={{ fontSize: "3.5rem", color: "#00C97A" }} />
                </CardContent>
              </Card>
              {s.avgStepOnResponse > 0 && (
                <StatCard
                  label="Msg média p/ responder"
                  value={`#${Math.round(s.avgStepOnResponse) + 1}`}
                  icon={TrendingUpIcon}
                  color="#008C4A"
                  sub="Qual mensagem gera retorno"
                />
              )}
            </Box>

            <Grid container spacing={2}>
              {/* Step Funnel */}
              <Grid item xs={12} md={7}>
                <Paper className={classes.sectionCard}>
                  <Typography className={classes.sectionTitle}>Funil por mensagem</Typography>
                  <Typography style={{ fontSize: "0.78rem", opacity: 0.55, marginBottom: 12 }}>
                    Quantas pessoas receberam cada mensagem e quantas responderam nela
                  </Typography>
                  {(data?.stepFunnel || []).length === 0 ? (
                    <Box textAlign="center" py={4} style={{ opacity: 0.4 }}>
                      <Typography>Sem dados suficientes</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={data.stepFunnel} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <RechartTooltip
                          formatter={(val, name) => [val, name === "sent" ? "Receberam" : "Responderam"]}
                          contentStyle={{ borderRadius: 8, fontSize: 12 }}
                        />
                        <Bar dataKey="sent" name="sent" fill={COLORS.sent} radius={[4,4,0,0]} maxBarSize={40}>
                          <LabelList dataKey="sent" position="top" style={{ fontSize: 10 }} />
                        </Bar>
                        <Bar dataKey="respondedAt" name="respondedAt" fill={COLORS.respondedAt} radius={[4,4,0,0]} maxBarSize={40}>
                          <LabelList dataKey="respondedAt" position="top" style={{ fontSize: 10 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  <Box display="flex" gap={2} mt={1} justifyContent="center">
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: COLORS.sent }} />
                      <Typography style={{ fontSize: "0.72rem" }}>Receberam</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: "#00C97A" }} />
                      <Typography style={{ fontSize: "0.72rem" }}>Responderam nessa msg</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Per-flow breakdown */}
              <Grid item xs={12} md={5}>
                <Paper className={classes.sectionCard}>
                  <Typography className={classes.sectionTitle}>Desempenho por fluxo</Typography>
                  {(data?.byFlow || []).length === 0 ? (
                    <Box textAlign="center" py={4} style={{ opacity: 0.4 }}>
                      <Typography>Nenhum fluxo com dados</Typography>
                    </Box>
                  ) : (
                    (data.byFlow || []).map(fw => (
                      <div key={fw.id} className={classes.flowRow}>
                        <div style={{ flex: 1 }}>
                          <Typography className={classes.flowName}>{fw.name}</Typography>
                          <Box display="flex" gap={1} mt={0.5} alignItems="center">
                            <span className={classes.badge} style={{ background: "rgba(23,35,28,.08)", color: "#17231C" }}>{fw.total} total</span>
                            <span className={classes.badge} style={{ background: "rgba(0,201,122,0.1)", color: "#00875A" }}>{fw.responded} ✓</span>
                            <span className={classes.badge} style={{ background: "rgba(239,68,68,0.1)", color: "#EF4444" }}>{fw.completed} ✗</span>
                            {fw.active > 0 && <span className={classes.badge} style={{ background: "rgba(245,158,11,0.1)", color: "#D97706" }}>{fw.active} ativo</span>}
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} mt={0.75}>
                            <div className={classes.rateBar} style={{ maxWidth: 120 }}>
                              <div className={classes.rateBarFill} style={{ width: `${fw.responseRate}%` }} />
                            </div>
                            <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#00875A" }}>
                              {fw.responseRate}% retorno
                            </Typography>
                          </Box>
                        </div>
                      </div>
                    ))
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default FloupDashboard;
